import React from 'react'
// libs
import NProgress from 'nprogress'
import { styled } from '@mui/material/styles'
// components
import Box from '@mui/material/Box'
import { Link as RouterLink } from 'react-router'
import UserBlock from 'layouts/MainLayout/UserBlock'
import MuiDrawer from '@mui/material/Drawer'
import GlobalSearchContainer from 'containers/GlobalSearch'
// context
import { MainLayoutAPIContext } from 'containers/MainLayout/layers/API'
// constants
import { LICENCES_NAMES } from 'constants/licences'
import { APP_BAR_HEIGHT, CLOSED_DRAWER_WIDTH, DRAWER_WIDTH } from 'constants/layout'
// hooks
import { useRouter } from 'hooks/useRouter'
import { useCurrentUser } from 'hooks/index'
// views
import { BigLogo, List, ListItem, ListItemIcon, ListItemText, ListWrapper, MiniLogo } from './views'
// themes
import { colors } from 'themes'
// utils
import get from 'lodash/get'
import { config } from './config'
import { configFilter } from './helpers'

const DrawerStyled = styled(MuiDrawer)<{
  open: boolean
  isFocused: boolean
}>(({ theme, open, isFocused }) => ({
  zIndex: 1300,
  flexShrink: 0,
  whiteSpace: 'nowrap',
  marginTop: APP_BAR_HEIGHT,
  width: open ? DRAWER_WIDTH : CLOSED_DRAWER_WIDTH,

  '& .MuiDrawer-paper': {
    borderRight: 'none',
    background: colors.darkBlue500,
    color: colors.white100,
    width: open ? DRAWER_WIDTH : CLOSED_DRAWER_WIDTH,
    overflowX: open ? (isFocused ? 'visible' : 'hidden') : 'hidden',
    transition: theme.transitions.create(['width', 'margin'], {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.leavingScreen
    })
  },

  '> div' : {
    overflow: 'unset'

  },

  '@media (max-width: 768px)': {
    position: 'fixed',
    top: '-64px',
    left: 0,
    height: '100%',
    transform: open ? 'translateX(0)' : `translateX(-${DRAWER_WIDTH}px)`,
    transition: theme.transitions.create('transform', {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.leavingScreen
    })
  }
}))

const Toolbar = styled(Box)(({ theme }) => ({
  display: 'flex',
  padding: '0 8px',
  cursor: 'pointer',
  alignItems: 'center',
  justifyContent: 'center',
  backgroundColor: colors.white100,
  ...theme.mixins.toolbar
}))

const Drawer: React.FC = () => {
  const user = useCurrentUser()
  const router = useRouter()

  const [isFocused, setFocus] = React.useState(false)

  const { isDrawerExpanded, toggleDrawer } = React.useContext(MainLayoutAPIContext)

  const { permissionsFromSecurityGroups: permissions } = user

  React.useEffect(() => {
    if (!isDrawerExpanded) {
      setFocus(false)
    }
  }, [isDrawerExpanded])

  return (
    <DrawerStyled variant='permanent' open={isDrawerExpanded} isFocused={isFocused}>
      <Toolbar onClick={toggleDrawer}>{isDrawerExpanded ? <BigLogo /> : <MiniLogo />}</Toolbar>

      <ListWrapper>
        {permissions.globalSearch && (
          <GlobalSearchContainer
            isFocused={isFocused}
            toggleDrawer={toggleDrawer}
            isExpanded={isDrawerExpanded}
            handleSetFocus={setFocus}
          />
        )}

        <List $withSearch={permissions.globalSearch}>
          {config
            .filter(({ label }) =>
              configFilter({
                label,
                permissions: {
                  ...permissions,
                  risksRegister: get(
                    get(user.company, 'licences', []).find(({ name }) => name === LICENCES_NAMES.FORMS),
                    'active',
                    false
                  )
                },
                isSkeftoAdmin: user.isAdmin
              })
            )
            .map(({ label, icon, path, getLink }) => {
              const linkProps = getLink() as any

              const href =
                linkProps.query?.slug
                  ? `${linkProps.href}/${linkProps.query.slug}`
                  : linkProps.href

              const isActive = path.some((p: string) => router.pathname.startsWith(p.split('/[[')[0].split('/[')[0]))

              return (
                <RouterLink
                  key={label}
                  to={href}
                  onClick={() => NProgress.start()}
                  style={{ textDecoration: 'none', color: 'inherit' }}>
                  <ListItem component='span' $isActive={isActive}>
                    <ListItemIcon>{icon}</ListItemIcon>
                    <ListItemText primary={label} disableTypography />
                  </ListItem>
                </RouterLink>
              )
            })}
        </List>

        <UserBlock isExpanded={isDrawerExpanded} />
      </ListWrapper>
    </DrawerStyled>
  )
}

export default Drawer
